import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mdVersHtml, dateHeure, usd, STATUT } from './MarcheAnalyseIa';
import './MarcheAnalyseIa.css';
import './Stock.css';   // st-seg

// ─── Panneau d'analyse IA d'un rapport (pièces, atelier) — 2026-10-04 ──────
// Commun aux analyses qui prennent la PÉRIODE DU RAPPORT (cumul de l'année ou
// mois choisis) : PR — Performance et APV — Performance. Choix des sociétés
// au lancement (par défaut celles du panneau de filtres), aperçu du texte
// exact envoyé, correspondance des pseudonymes gardée sur le serveur,
// historique avec la période et les sociétés de chaque analyse.
// `api` : { liste, lire, apercu(societes, filtres), lancer(societes, filtres) }.

export default function AnalyseIaRapport({ api, titre, sousTitre, prefixeFichier, selection, filtres, onFermer }) {
  const [liste, setListe] = useState(null);
  const [editable, setEditable] = useState(false);
  const [societes, setSocietes] = useState([]);
  const [choix, setChoix] = useState([]);
  const [courante, setCourante] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState('');
  const [attente, setAttente] = useState(false);
  const [brut, setBrut] = useState(false);   // texte tel que reçu (pseudonymisé)
  const minuteur = useRef(null);

  const chargerListe = useCallback(() => api.liste().then((r) => {
    setListe(r.data.analyses); setEditable(r.data.editable); setSocietes(r.data.societes || []);
    return r.data;
  }), [api]);
  const ouvrir = useCallback((id) => {
    clearTimeout(minuteur.current);
    api.lire(id).then((r) => {
      setCourante(r.data); setApercu(null);
      if (r.data.statut === 'en_cours') minuteur.current = setTimeout(() => ouvrir(id), 2000);
      else chargerListe();
    }).catch((e) => setErreur(e.response?.data?.error || 'Analyse introuvable'));
  }, [api, chargerListe]);

  useEffect(() => {
    chargerListe().then((d) => {
      const dispo = (d.societes || []).map((s) => s.code);
      const defaut = (selection || []).filter((c) => dispo.includes(c));
      setChoix(defaut.length ? defaut : dispo);
      if (d.analyses && d.analyses.length) ouvrir(d.analyses[0].id);
    }).catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement'));
    return () => clearTimeout(minuteur.current);
  }, [chargerListe, ouvrir]);   // eslint-disable-line react-hooks/exhaustive-deps

  const basculer = (code) => setChoix((l) => (l.includes(code) ? l.filter((c) => c !== code) : [...l, code]));
  const generer = async () => {
    setErreur(''); setAttente(true);
    try { const r = await api.lancer(choix, filtres); await chargerListe(); ouvrir(r.data.id); }
    catch (e) { setErreur(e.response?.data?.error || 'Génération impossible'); }
    finally { setAttente(false); }
  };
  const voirApercu = async () => {
    setErreur(''); setAttente(true);
    try { const r = await api.apercu(choix, filtres); setApercu(r.data); setCourante(null); }
    catch (e) { setErreur(e.response?.data?.error || 'Aperçu impossible'); }
    finally { setAttente(false); }
  };
  const exporter = () => {
    const b = new Blob([courante.contenu || ''], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(b);
    a.download = `${prefixeFichier}-${String(courante.cree_le).slice(0, 10)}.md`; a.click(); URL.revokeObjectURL(a.href);
  };
  const correspondance = apercu?.correspondance
    ? Object.entries(apercu.correspondance.societes || {})
    : [];

  return (
    <div className="ia-voile" onClick={onFermer}>
      <aside className="ia-panneau" onClick={(e) => e.stopPropagation()} aria-label={titre}>
        <div className="ia-tete">
          <div>
            <h2>{titre}</h2>
            <div className="ia-sous">{sousTitre}</div>
          </div>
          <button type="button" className="ia-fermer" onClick={onFermer} aria-label="Fermer" title="Fermer">✕</button>
        </div>

        {editable && (
          <div className="ia-actions">
            <span className="ia-note">Sociétés :</span>
            <span className="st-seg">{societes.map((s) => (
              <button key={s.code} type="button" className={choix.includes(s.code) ? 'on' : ''} onClick={() => basculer(s.code)}>{s.nom}</button>
            ))}</span>
          </div>
        )}
        <div className="ia-actions">
          {editable && <button type="button" className="ia-btn ia-btn-p" disabled={attente || !choix.length || courante?.statut === 'en_cours'} onClick={generer}>Générer une analyse</button>}
          {editable && <button type="button" className="ia-btn" disabled={attente || !choix.length} onClick={voirApercu}>Aperçu du dossier</button>}
          {(courante?.statut === 'termine' || courante?.statut === 'tronque') && <button type="button" className="ia-btn" onClick={exporter}>⬇ Markdown</button>}
          {!editable && <span className="ia-note">La génération est réservée aux administrateurs ; l'historique est consultable.</span>}
        </div>
        {erreur && <div className="ia-erreur">⚠️ {erreur}</div>}

        <div className="ia-corps">
          <div className="ia-histo">
            <div className="ia-histo-t">Historique</div>
            {liste === null ? <div className="ia-note">Chargement…</div> : !liste.length ? <div className="ia-note">Aucune analyse pour l'instant.</div> : liste.map((a) => (
              <button type="button" key={a.id} className={`ia-histo-l${courante?.id === a.id ? ' on' : ''}`} onClick={() => ouvrir(a.id)}>
                <b>{dateHeure(a.cree_le)}</b>
                <span>{a.periode}</span>
                <span>{(a.noms || []).length === societes.length && societes.length > 1 ? 'toutes les sociétés' : (a.noms || []).join(', ')}</span>
                {a.cout_usd != null && <span>{usd(a.cout_usd)}</span>}
                <span className={`ia-statut ia-${a.statut}`}>{STATUT[a.statut] || a.statut}</span>
              </button>
            ))}
          </div>

          <div className="ia-contenu">
            {apercu ? (
              <>
                <div className="ia-meta">Aperçu — rien n'a été envoyé · {apercu.periode} · {apercu.modele} · prompt {apercu.version_prompt} · {Math.round(apercu.caracteres / 1000)} k caractères (≈ {Math.round(apercu.caracteres / 3200)} k tokens)</div>
                <div className={apercu.bloque ? 'ia-erreur' : 'ia-cours'}>
                  {apercu.bloque ? '⚠️ Envoi bloqué — ' : 'Contrôle de pseudonymisation : '}{apercu.controle}
                </div>
                <details open><summary>Correspondance des pseudonymes — gardée sur le serveur, jamais envoyée</summary>
                  <table className="ia-table"><tbody>{correspondance.map(([p, n]) => <tr key={p}><td>{p}</td><td>{n}</td></tr>)}</tbody></table>
                </details>
                <details><summary>Consigne système (envoyée)</summary><pre>{apercu.system}</pre></details>
                <details><summary>Message et dossier chiffré (envoyés)</summary><pre>{apercu.user}</pre></details>
              </>
            ) : !courante ? (
              <div className="ia-note">{editable ? 'Choisissez les sociétés, puis générez une analyse — ou ouvrez-en une dans l\'historique.' : 'Choisissez une analyse dans l\'historique.'}</div>
            ) : (
              <>
                <div className="ia-meta">
                  {dateHeure(courante.cree_le)} · {courante.auteur} · {(courante.noms || []).join(', ')} · {courante.periode} · {courante.modele} · prompt {courante.version_prompt}
                  {courante.statut !== 'en_cours' && courante.tokens_entree ? ` · ${Math.round(courante.tokens_entree / 1000)} k tokens lus, ${Math.round((courante.tokens_sortie || 0) / 1000)} k écrits · ${Math.round((courante.duree_ms || 0) / 1000)} s` : ''}
                  {courante.cout_usd != null ? <> · <b>{usd(courante.cout_usd)}</b></> : null}
                </div>
                {courante.contenu && (
                  <div className="ia-actions">
                    <span className="st-seg">
                      <button type="button" className={!brut ? 'on' : ''} onClick={() => setBrut(false)}>Noms réels</button>
                      <button type="button" className={brut ? 'on' : ''} onClick={() => setBrut(true)}>Tel que reçu (pseudonymisé)</button>
                    </span>
                  </div>
                )}
                {(courante.statut === 'echec' || courante.statut === 'tronque') && <div className="ia-erreur">⚠️ {courante.erreur}</div>}
                {courante.statut === 'en_cours' && <div className="ia-cours">Analyse en cours de rédaction… {courante.contenu ? '' : 'Le modèle étudie le dossier (une à deux minutes).'}</div>}
                <div className="ia-rapport" dangerouslySetInnerHTML={{ __html: mdVersHtml(brut ? courante.contenu_pseudonymise : courante.contenu) }} />
              </>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
